import axios from "axios";
import { getOnlyNumbers } from "./../constants/composeString";
import { CompanyData, ValidCompanyDataErrors } from "./../types/user";
import { Endpoints } from "../constants/constants";
import api from "../utils/api";
import apiWithoutToken from "../utils/apiWithoutToken";
import { InputErrors } from "../constants/valids";
import { UserTypes } from "../constants/user";

type ErrorType = {
  status?: string;
  message?: string;
};

type RegisterUserData = {
  data: CompanyData | [];
  errors: (ValidCompanyDataErrors & ErrorType) | null;
};

type InnDataAnswer = {
  kpp: string;
  management: {
    name: string;
  };
  name: {
    full_with_opf: string;
    short_with_opf: string;
  };
  inn: string;
  ogrn: string;
  type: "LEGAL" | "INDIVIDUAL" | false;
  fio: {
    name: string;
    patronymic: string;
    surname: string;
  };
  okved: string | null;
};

type InnRequestAnswer = {
  data: {
    data: Array<{ data: InnDataAnswer }>;
    success: boolean;
  };
};

type TaxpayerStatusAnswer = {
  status: boolean;
  message: string;
};

const TAXPAYER_STATUS_URL = "https://statusnpd.nalog.ru/api/v1/tracker/taxpayer_status";

export class RegistrationService {
  static async registarData(data: CompanyData): Promise<RegisterUserData> {
    const result: RegisterUserData = {
      data: [],
      errors: null,
    };
    const response = await apiWithoutToken.post(Endpoints.REGISTER, {
      ...data,
      phone: getOnlyNumbers(data.phone),
    });
    const redData = response.data;
    if (redData.errors?.status === 404) {
      result.errors = { inn: [InputErrors.NOT_FOUND_INN] };
    } else {
      result.data = redData.data;
      result.errors = redData.success ? null : redData.errors;
    }
    return result;
  }

  static async checkSelfEmployedStatus(inn: string): Promise<boolean> {
    try {
      const requestDate = new Date().toISOString().substring(0, 10);
      const response = await axios.post<TaxpayerStatusAnswer>(
        TAXPAYER_STATUS_URL,
        { inn, requestDate },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return !!response.data?.status;
    } catch {
      return false;
    }
  }

  static async getOrganisationInfoByInn(inn: string) {
    const response: InnRequestAnswer = await api.post(
      `${Endpoints.ORG_DATA}?inn=${inn}`
    );
    const orgItem = response.data?.data?.[0]?.data;

    if (response.data?.success && orgItem) {
      const formOfOwnership = orgItem.type === "LEGAL"
        ? UserTypes.legal
        : UserTypes.individual;

      return {
        "name-organization": orgItem.name?.short_with_opf || "",
        "full-name": orgItem.name?.full_with_opf || "",
        "general-director": orgItem.management?.name || "",
        inn: orgItem?.inn || "",
        ogrn: orgItem?.ogrn || "",
        kpp: orgItem?.kpp || "",
        "type-of-ownership": formOfOwnership,
        "first-name": orgItem?.fio?.name || "",
        surname: orgItem?.fio?.surname || "",
        patronymic: orgItem?.fio?.patronymic || "",
        okveds: orgItem?.okved || "",
      };
    }

    const isSelfEmployed = await this.checkSelfEmployedStatus(inn);
    if (isSelfEmployed) {
      return {
        inn: inn,
        "type-of-ownership": UserTypes.selfEmployed,
        "name-organization": "",
        "full-name": "",
        "general-director": "",
        ogrn: "",
        kpp: "",
        "first-name": "",
        surname: "",
        patronymic: "",
        okveds: "",
        "passport-series": "",
        "passport-number": "",
        "passport-issue-date": "",
      };
    }

    return null;
  }
}
